import { useEffect, useState } from "react";
import type { Ordre, OrdreIntervention } from "types/ordre";
import { isAPrevoir } from "types/ordre";
import { ordresApi } from "@services/api/ordres";

// Interventions (with hours / "à prévoir") live on the ordre, not on the
// lightweight job/appointment record, so hover panels fetch them lazily by
// ordre_id and cache the result to keep repeat hovers instant. Shared by the
// planning reception tooltip (AppointmentTooltip) and the atelier grid job
// tooltip so both render the "Interventions (N)" block identically.
const interventionsCache = new Map<string, OrdreIntervention[]>();
// Même chargement pour le numéro d'ordre de la bulle : une seule requête par ordre.
const numCache = new Map<string, string>();
const plateCache = new Map<string, string>();
const inflight = new Map<string, Promise<Ordre>>();

function loadOrdre(ordreId: string): Promise<Ordre> {
  let p = inflight.get(ordreId);
  if (!p) {
    p = ordresApi.get(ordreId).then((ordre) => {
      interventionsCache.set(ordreId, ordre.interventions || []);
      numCache.set(ordreId, ordre.num || "");
      plateCache.set(ordreId, ordre.immatriculation || "");
      return ordre;
    });
    p.catch(() => inflight.delete(ordreId));
    inflight.set(ordreId, p);
  }
  return p;
}

/** Numéro d'ordre affichable : pas les numéros provisoires (TEMP-…) ni l'identifiant
 *  de dossier ServiceBox à 13 chiffres que l'ancien import stockait à la place de l'OR. */
function displayableNum(num: string | undefined): string {
  const n = (num || "").trim();
  if (!n || /^TEMP-/i.test(n) || /^\d{11,}$/.test(n)) return "";
  return n;
}

/** Numéro de l'ordre, à côté du nom du client dans les bulles de l'atelier. */
export function OrdreNumHint({ ordreId }: { ordreId?: string }) {
  const [num, setNum] = useState<string>(() => (ordreId ? numCache.get(ordreId) ?? "" : ""));
  useEffect(() => {
    if (!ordreId) return;
    if (numCache.has(ordreId)) { setNum(numCache.get(ordreId) || ""); return; }
    let alive = true;
    loadOrdre(ordreId).then((o) => { if (alive) setNum(o.num || ""); }).catch(() => {});
    return () => { alive = false; };
  }, [ordreId]);
  const shown = displayableNum(num);
  if (!shown) return null;
  return <span className="text-h5 font-normal text-neutral-300 ml-2 whitespace-nowrap">{shown}</span>;
}

/** Immatriculation de la bulle : celle du travail, sinon celle de l'ordre (un travail
 *  posé depuis une charge n'emporte pas la plaque). */
export function OrdrePlateHint({ plate, ordreId }: { plate?: string; ordreId?: string }) {
  const [fromOrdre, setFromOrdre] = useState<string>(() => (ordreId ? plateCache.get(ordreId) ?? "" : ""));
  useEffect(() => {
    if (plate || !ordreId) return;
    if (plateCache.has(ordreId)) { setFromOrdre(plateCache.get(ordreId) || ""); return; }
    let alive = true;
    loadOrdre(ordreId).then((o) => { if (alive) setFromOrdre(o.immatriculation || ""); }).catch(() => {});
    return () => { alive = false; };
  }, [plate, ordreId]);
  const shown = plate || fromOrdre;
  if (!shown) return null;
  return <div className="text-h5 text-neutral-300">{shown}</div>;
}

/**
 * The dark-tooltip "Interventions (N)" block: a vertical list of intervention
 * labels with their estimated hours, à-prévoir styling, and a "+N autre(s)"
 * overflow. Falls back to plain string labels (no hours) when the ordre can't be
 * loaded — e.g. an atelier job that only carries labels.
 */
export default function OrdreInterventionsHint({
  ordreId,
  fallbackLabels,
}: {
  ordreId?: string;
  fallbackLabels?: string[];
}) {
  const [items, setItems] = useState<OrdreIntervention[] | null>(
    () => (ordreId ? interventionsCache.get(ordreId) ?? null : null),
  );

  useEffect(() => {
    if (!ordreId || interventionsCache.has(ordreId)) return;
    let alive = true;
    loadOrdre(ordreId)
      .then((ordre) => {
        if (alive) setItems(ordre.interventions || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ordreId]);

  const labels = fallbackLabels ?? [];
  // Nothing to show and nothing to load → render nothing.
  if (!ordreId && labels.length === 0) return null;
  // ordre known but not fetched yet, and no fallback → wait silently.
  if (items === null && labels.length === 0) return null;

  const count = items ? items.length : labels.length;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-700" style={{ whiteSpace: "normal" }}>
      <div className="text-h5 text-neutral-400 font-medium mb-1">Interventions ({count})</div>
      {count === 0 ? (
        <div className="text-h5 text-neutral-500 italic">Aucune intervention</div>
      ) : items ? (
        <div className="flex flex-col gap-0.5">
          {items.slice(0, 8).map((it) => {
            const aPrevoir = isAPrevoir(it);
            return (
              <div key={it.id} className="flex flex-row items-baseline justify-between gap-2">
                <span
                  className={`text-h5 truncate ${aPrevoir ? "text-amber-300/80 line-through decoration-amber-400/50" : "text-white"}`}
                >
                  {it.label}
                  {aPrevoir ? " · à prévoir" : ""}
                </span>
                {it.estimated_hours ? (
                  <span className="text-h5 text-neutral-400 flex-shrink-0">{+Number(it.estimated_hours || 0).toFixed(2)}h</span>
                ) : null}
              </div>
            );
          })}
          {items.length > 8 && (
            <div className="text-h5 text-neutral-500">+{items.length - 8} autre(s)</div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {labels.slice(0, 8).map((l, i) => (
            <div key={i} className="text-h5 text-white truncate">
              {l}
            </div>
          ))}
          {labels.length > 8 && (
            <div className="text-h5 text-neutral-500">+{labels.length - 8} autre(s)</div>
          )}
        </div>
      )}
    </div>
  );
}
